import { appConfig } from 'config/environment/appConfig'

import { HttpService } from 'services/HttpService'
import { getQueryUrl } from 'services/utils/getQueryUrl'
import convertKeysToCamelCase from 'utils/helpers/convertKeysToCamelCase'
import { PaginationType, ResponseListData } from 'shared/types/types'

import {
  AddCCTVInfoRequest,
  AddOrderPayment,
  AddOrderPaymentDTO,
  CameraVendorListDTO,
  CCTVInfoDTO,
  CheckOrderAddressRequest,
  Coordinate,
  CreateOrderRequest,
  FurnitureOrderType,
  GetPaymentDeadlineDTO,
  GetTariffDTO,
  GetTariffsRequest,
  OrderDTO,
  OrderMessageDTO,
  OrderPaymentStatus,
  OrderReadyActivateRequest,
  PublishOrderDTO,
  SubsidyInfo,
  SubsidyTypeDTO,
  UpdateOrderRequest,
  ValidateCCTVInfoDTO,
  ValidateCCTVInfoRequest,
  VendorList,
} from './types'

const service = new HttpService()
const discoveryService = new HttpService(appConfig.DISCOVERY_URL)
const camerasService = new HttpService(appConfig.CAMERAS_URL)

export const ordersApi = {
  createOrder: (data: CreateOrderRequest): Promise<{ data: number }> => {
    return discoveryService.post('/api/v2/pickpoint/order/add', {
      body: {
        area: data.area,
        stock_area: data.warehouseArea,
        actual_urls: data.actualUrls,
        latitude: data.latitude,
        longitude: data.longitude,
        dressing_rooms_count: data.dressingRoomsCount,
        country: data.country,
        phone: data.phone,
        locations: data.locations,
        access_type: data.accessType,
        timetable: data.timetable,
        description: data.description,
        courier_way: data.courierWay,
        ex_pickpoint_id: data.exPickPointId,
        city_id: data.cityId,
        city_name: data.cityName,
        house: data.house,
        street: data.street,
        region: data.region,
        district: data.district,
        celling_height: data.cellingHeight,
        subsidy_hex_type: data.subsidyHexType,
        type_point: data.pickPointType,
        description_pickpoint_type: '',
        has_dress_room: true,
        is_branded: true,
        /* для разделения заявки с промо и мобилки */
        client_type: 'CLIENT_TYPE_SITE',
      },
    })
  },

  updateOrder: (data: UpdateOrderRequest): Promise<{ data: boolean }> => {
    const patch = {
      house: data.house,
      street: data.street,
      latitude: data.latitude,
      longitude: data.longitude,
      description: data.description,
      courier_way: data.courierWay,
      metadata: data.courierWay,
      type_point: data.pickPointType,
      timetable: data.timetable,
      area: data.area,
      stock_area: data.warehouseArea,
      dressing_rooms_count: data.dressingRoomsCount,
      celling_height: data.cellingHeight,
      comment: data.comment,
      actual_urls: data.actualUrls,
      signboard_size: data.signboardSize,
      signboard_format: data.signboardFormat,
      subsidy_hex_type: data.subsidyHexType,
      access_type: data.couriersAccess,
      owner_gln: data.ownerGln,
      pickpoint_gln: data.glnPvz,
      mol_name: data.molName,
    }

    const updatedFields = Object.entries(patch)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key]) => key)

    return discoveryService.post(`/api/v2/pickpoint/order/${data.id}/update`, {
      body: {
        ...patch,
        update_fields: updatedFields,
      },
    })
  },

  deleteOrder: async (id: number): Promise<{ data: boolean }> => {
    return await discoveryService.post(`/api/v2/pickpoint/order/${id}/delete`, {
      body: {
        id,
      },
    })
  },

  publishOrder: (id: number): Promise<PublishOrderDTO> => {
    return discoveryService.post(`/api/v2/pickpoint/order/${id}/publish`, {
      body: {
        id,
      },
    })
  },

  getTariffs: async ({ area, lat, lon, typePoint }: GetTariffsRequest): Promise<GetTariffDTO | undefined> => {
    const result = await discoveryService.post<ResponseListData<GetTariffDTO[]>>('api/v4/tariff_2', {
      body: {
        latitude: lat,
        longitude: lon,
        area,
        type_point: typePoint,
      },
    })

    return result.data.length > 0 ? convertKeysToCamelCase(result.data[0]) : undefined
  },

  getOrderList: async ({ page, pageSize }: PaginationType): Promise<ResponseListData<OrderDTO[]>> => {
    const queryUrl = getQueryUrl({
      page_size: pageSize,
      page,
      'filter.order_field': 'created',
      'filter.sort_desc': true,
      'filter.is_deleted': true,
    })

    const result = await discoveryService.get<ResponseListData<OrderDTO[]>>(`/api/v2/pickpoint/order/list?${queryUrl}`)

    return result ? convertKeysToCamelCase(result) : undefined
  },

  addOrderPayment: async (orderId: number): Promise<AddOrderPayment> => {
    const result = await discoveryService.post<AddOrderPaymentDTO>('/api/v2/pickpoint/order/check/add', {
      body: {
        client_type: 'CLIENT_TYPE_SITE',
        pickpoint_order_id: orderId,
      },
    })

    const converted = convertKeysToCamelCase(result) as AddOrderPaymentDTO

    return {
      paymentUrl: converted.paymentUrl,
      data: {
        country: converted.data.currency,
        sum: converted.data.sumRub,
      },
    }
  },

  checkOrderPayment: async (orderId: number): Promise<{ status: OrderPaymentStatus }> => {
    const result = await discoveryService.get<ResponseListData<{ status: OrderPaymentStatus }>>(
      `/api/v2/pickpoint/order/check/${orderId}`,
    )

    return result.data
  },

  getSecretImages: async (url: string): Promise<string> => {
    const result = await service.get<{ file: string }>(url)
    return result.file
  },

  getOrderMessages: async (orderId: number): Promise<OrderMessageDTO[]> => {
    const result = await discoveryService.get<ResponseListData<OrderMessageDTO>>(
      `/api/v2/pickpoint/order/pickpoint-information/list/${orderId}`,
    )

    return convertKeysToCamelCase(result.data)
  },

  getVendorList: async (): Promise<VendorList[]> => {
    const result = await camerasService.get<CameraVendorListDTO>('/api/v2/manage/camera/token/vendor/list')
    return convertKeysToCamelCase(result?.vendor_list)
  },

  validateCCTVInfo: async ({
    externalPickPointId,
    vendor,
    url,
    login,
    password,
    token,
    description,
  }: ValidateCCTVInfoRequest): Promise<ValidateCCTVInfoDTO> => {
    const result = await camerasService.post<ValidateCCTVInfoDTO>('/api/promo/cameras/validate/check', {
      body: {
        external_pickpoint_id: externalPickPointId,
        vendor,
        url,
        login,
        password,
        token,
        description,
      },
    })

    return convertKeysToCamelCase(result)
  },

  addCCTVInfo: async ({
    orderId,
    url,
    login,
    password,
    description,
  }: AddCCTVInfoRequest): Promise<{ camera: CCTVInfoDTO }> => {
    const result = await discoveryService.post<{ camera: CCTVInfoDTO }>(`/api/v2/order/cameras/${orderId}`, {
      body: {
        url,
        login,
        password,
        description,
      },
    })

    return convertKeysToCamelCase(result)
  },

  getCCTVInfo: async (orderId: number): Promise<CCTVInfoDTO | null> => {
    const result = await discoveryService.get<ResponseListData<CCTVInfoDTO | null>>(`/api/v2/order/cameras/${orderId}`)
    return convertKeysToCamelCase(result?.data)
  },

  makeOrderReadyActivate: async (data: OrderReadyActivateRequest): Promise<boolean> => {
    const result = await discoveryService.post<ResponseListData<boolean>>(
      `api/v2/pickpoint/order/${data.orderId}/ready-activate`,
      {
        body: {
          id: data.orderId,
          owner_gln: data.ownerGln,
          pickpoint_gln: data.glnPvz,
          mol_name: data.molName,
        },
      },
    )

    return result.data
  },

  confirmOrder: async (orderId: number, willOpen: boolean) => {
    const queryUrl = getQueryUrl({
      will_open: willOpen,
    })

    return await discoveryService.post(`api/v4/order/open/confirm/${orderId}?${queryUrl}`)
  },

  checkOrderAddress: async (data: CheckOrderAddressRequest): Promise<boolean> => {
    const result = await discoveryService.post<ResponseListData<boolean>>('/api/v4/check/address', {
      body: {
        lat_long: {
          latitude: data.coordinates.latitude,
          longitude: data.coordinates.longitude,
        },
        country: data.country,
        city_id: data.cityId,
        city_name: data.cityName,
        house: data.house,
        street: data.street,
        region: data.region,
      },
    })

    return result.data
  },

  assignReferralCode: async (orderId: number, referralCode: string): Promise<boolean> => {
    const result = await discoveryService.post<{ success: boolean }>(`/api/v4/pickpoint/referral/${orderId}/assign`, {
      body: {
        referral_code: referralCode,
      },
    })

    return result.success
  },

  getFurnitureOrderType: async (orderId: number): Promise<{ orderScheme: FurnitureOrderType }> => {
    const result = await discoveryService.get<ResponseListData<{ orderScheme: FurnitureOrderType }>>(
      `/api/v4/order/${orderId}/furniture`,
    )
    return convertKeysToCamelCase(result?.data)
  },

  getSubsidy: async (coordinates: Coordinate, tariff: number): Promise<SubsidyInfo[]> => {
    const queryUrl = getQueryUrl({
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      tariff,
    })

    const result = await discoveryService.get<SubsidyTypeDTO>(`/api/v4/pickpoint/subsidy?${queryUrl}`)

    return convertKeysToCamelCase(result?.subsidy_info)
  },

  getPaymentDeadline: async (orderId: number): Promise<{ seconds: number }> => {
    const result = await discoveryService.get<GetPaymentDeadlineDTO>(`/api/v4/pickpoint/${orderId}/removal_time`)
    return result?.time_remaining
  },
}
